import { ChevronDown } from "lucide-react";
import { NavLink, Outlet } from "react-router-dom";

import { usePendingPresentations } from "@/api/presentation-review";
import { usePendingReview } from "@/api/review";
import { useSupabase } from "@/auth/SupabaseProvider";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { Logo } from "./Logo";

/**
 * Top-level layout for authenticated routes. Header (logo + tab nav +
 * user menu) above a centred content column. Mirrors the marketing
 * site's nav rhythm — same logo, same gutter width, same letter-spaced
 * mono labels.
 */
export function Layout() {
  const { client, session } = useSupabase();
  const email = session?.user.email ?? "";

  // Drive the Review tab's count badge from the same query the `/review`
  // page uses. TanStack's cache means visiting either surface keeps the
  // badge fresh on every other page for free; the badge silently shows
  // nothing while the first fetch is in flight or if it errors.
  const pendingReview = usePendingReview();
  const reviewRemaining = pendingReview.data
    ? pendingReview.data.batches.reduce(
        (acc, b) =>
          acc + b.items.filter((i) => i.existingDecision === null).length,
        0,
      )
    : 0;

  // Presentations tab badge — count of pending presentations. Same
  // TanStack cache pattern; the `/presentations` route re-uses the
  // wider `useRecentPresentations` query (pending + recent), but the
  // pending-only endpoint is cheaper for the header badge which
  // renders on every page. Issue #575, finding #1.
  const pendingPresentations = usePendingPresentations();
  const presentationsPending = pendingPresentations.data
    ? pendingPresentations.data.presentations.length
    : 0;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border bg-bg-dark/80 backdrop-blur">
        <div className="max-w-[1200px] mx-auto px-6 h-[72px] flex items-center justify-between gap-6">
          <NavLink to="/jobs" className="shrink-0" aria-label="Home">
            <Logo width={140} />
          </NavLink>
          <nav className="flex items-center gap-1">
            <NavTab to="/jobs">Jobs</NavTab>
            <NavTab to="/run">Run</NavTab>
            <NavTab to="/review" badgeCount={reviewRemaining}>
              Review
            </NavTab>
            <NavTab to="/presentations" badgeCount={presentationsPending}>
              Presentations
            </NavTab>
            <NavTab to="/catalogue">Catalogue</NavTab>
            <NavTab to="/cost">Cost</NavTab>
            <NavTab to="/access-codes">Codes</NavTab>
            <NavTab to="/prompts">Prompts</NavTab>
            <NavTab to="/report">Report</NavTab>
            <NavTab to="/settings">Settings</NavTab>
          </nav>
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(
                "flex items-center gap-2 text-sm text-text-muted hover:text-text rounded px-2 py-1",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              )}
            >
              <span className="font-mono text-xs truncate max-w-[180px]">{email}</span>
              <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Signed in</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  void client.auth.signOut();
                }}
              >
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <main className="flex-1 max-w-[1200px] w-full mx-auto px-6 py-10">
        <Outlet />
      </main>
    </div>
  );
}

function NavTab({
  to,
  children,
  badgeCount,
}: {
  to: string;
  children: React.ReactNode;
  /** When > 0, render a small accent badge after the label (e.g. pending-review count). */
  badgeCount?: number;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          "px-3 py-1.5 text-sm font-mono uppercase tracking-label transition-colors inline-flex items-center gap-2",
          isActive ? "text-accent border-b border-accent" : "text-text-muted hover:text-text",
        )
      }
    >
      <span>{children}</span>
      {badgeCount && badgeCount > 0 ? (
        <Badge variant="accent" aria-label={`${badgeCount} pending`}>
          {badgeCount}
        </Badge>
      ) : null}
    </NavLink>
  );
}
